import type { OnDestroy, OnInit } from '@angular/core';
import type { Notif } from '@commons/bo-ui/notifications/notif';

import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NotifStatus } from '@commons/bo-ui/notifications/notif-status.enum';
import { NotificationsService } from '@commons/bo-ui/notifications/notifications.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  standalone: true,
  imports: [NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('animateToasts', [
      state('void', style({ opacity: 0 })),
      transition(':enter, :leave', [animate('0.3s ease')]),
    ]),
  ],
})
export class WzbBoNotificationsComponent implements OnInit, OnDestroy {
  private readonly notificationService = inject(NotificationsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly domSanitizer = inject(DomSanitizer);

  private readonly destroyed$: Subject<boolean | null> = new Subject();

  notifications: Notif[] = [];
  NotifStatus = NotifStatus;
  duration = 5000;

  ngOnInit(): void {
    this.notificationService.notification.pipe(takeUntil(this.destroyed$)).subscribe((notification: Notif) => {
      this.updateNotification(notification);
    });

    this.notificationService.clearToasts.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.clearToasts();
    });

    this.notificationService.clearLastToast.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.clearLastToast();
    });
  }

  private updateNotification(notification: Notif) {
    notification.message = this.domSanitizer.sanitize(SecurityContext.HTML, notification.message)!;

    this.notifications.unshift(notification);
    this.cdr.detectChanges();

    this.notifications[0].timeout = window.setTimeout(() => {
      const index = this.notifications.indexOf(notification);
      this.removeNotification(index);

      if (notification.callback) {
        notification.callback();
      }
    }, notification.duration || this.duration);
  }

  removeNotification(index: number): void {
    this.notifications.splice(index, 1);
    this.cdr.detectChanges();
  }

  cancelNotification(index: number, isClickAction = false): void {
    window.clearTimeout(this.notifications[index].timeout);

    if (!isClickAction && this.notifications[index].cancelCallback) {
      this.notifications[index].cancelCallback();
    }

    this.removeNotification(index);
  }

  private clearLastToast(): void {
    this.notifications.pop();
    this.cdr.detectChanges();
  }

  private clearToasts(): void {
    this.notifications = [];
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(null);
    this.destroyed$.complete();
  }
}
